import type { VesselInfo } from "@redux/features/selectedVessel/selectedVesselSlice"
import { common } from "@utils/common"
import type { PlantObject } from "api"

type VesselReference = Pick<VesselInfo, "name" | "plantId"> & { owner?: string | null }

const findPlantDataByDisplayName = (name: string): PlantObject | undefined => {
	return (
		common.plantDataFromName.get(name) ??
		Array.from(common.plantDataFromName.values()).find(
			(plant) => plant.display_name.toLowerCase() === name.toLowerCase()
		)
	)
}

const findPlantDataById = (plantId: number): PlantObject | undefined =>
	Array.from(common.plantDataFromOwnerAndId.values()).find((plant) => plant.id === plantId)

export const getPlantDataForVessel = (
	vessel: VesselReference,
	companyName?: string
): PlantObject | undefined => {
	const owner = typeof vessel.owner === "string" ? vessel.owner : undefined
	const ownerAndId =
		owner !== undefined
			? common.plantDataFromOwnerAndId.get(`${owner.toLowerCase()}/${vessel.plantId}`)
			: undefined
	if (ownerAndId !== undefined) {
		return ownerAndId
	}

	const companyAndId =
		companyName !== undefined
			? common.plantDataFromOwnerAndId.get(`${companyName.toLowerCase()}/${vessel.plantId}`)
			: undefined
	if (companyAndId !== undefined) {
		return companyAndId
	}

	const plantDataById = findPlantDataById(vessel.plantId)
	if (plantDataById !== undefined) {
		return plantDataById
	}

	const ownerAndName =
		owner !== undefined
			? common.plantDataFromOwnerAndName.get(
					`${owner.toLowerCase()}/${vessel.name.toLowerCase()}`
				)
			: undefined
	if (ownerAndName !== undefined) {
		return ownerAndName
	}

	const companyAndName =
		companyName !== undefined
			? common.plantDataFromOwnerAndName.get(
					`${companyName.toLowerCase()}/${vessel.name.toLowerCase()}`
				)
			: undefined
	if (companyAndName !== undefined) {
		return companyAndName
	}

	return findPlantDataByDisplayName(vessel.name)
}

export const getVesselDisplayName = (vessel: VesselReference, companyName?: string): string =>
	getPlantDataForVessel(vessel, companyName)?.display_name ?? vessel.name

export const vesselMatchesSearch = (
	vessel: VesselReference,
	searchVessel: string | undefined,
	companyName?: string
): boolean => {
	if (searchVessel === undefined || searchVessel === "") {
		return false
	}

	const search = searchVessel.toLowerCase()
	const plantData = getPlantDataForVessel(vessel, companyName)

	return (
		vessel.name.toLowerCase() === search ||
		vessel.plantId.toString() === search ||
		plantData?.display_name.toLowerCase() === search
	)
}
